import { create } from "zustand";
import {
  type ClaudeProject,
  type ClaudeSession,
  type ClaudeMessage,
  type SessionTokenStats,
  type ProjectStatsSummary,
  type SessionComparison,
  AppErrorType,
} from "@anthropic/shared";
import type { AppError, SearchFilters } from "@anthropic/shared";
import * as api from "../api/httpClient.js";

// Analytics types - must match types/analytics.ts
type AnalyticsViewType = "messages" | "tokenStats" | "analytics";

interface AnalyticsState {
  currentView: AnalyticsViewType;
  projectSummary: ProjectStatsSummary | null;
  sessionComparison: SessionComparison | null;
  isLoadingProjectSummary: boolean;
  isLoadingSessionComparison: boolean;
  projectSummaryError: string | null;
  sessionComparisonError: string | null;
}

const initialAnalyticsState: AnalyticsState = {
  currentView: "messages",
  projectSummary: null,
  sessionComparison: null,
  isLoadingProjectSummary: false,
  isLoadingSessionComparison: false,
  projectSummaryError: null,
  sessionComparisonError: null,
};

interface PaginationState {
  currentOffset: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
}

// Message order type - "asc" = oldest first (top to bottom), "desc" = newest first (bottom to top)
type MessageOrderType = "asc" | "desc";

interface AppState {
  claudePath: string;
  projects: ClaudeProject[];
  selectedProject: ClaudeProject | null;
  sessions: ClaudeSession[];
  selectedSession: ClaudeSession | null;
  messages: ClaudeMessage[];
  pagination: PaginationState;
  searchQuery: string;
  searchResults: ClaudeMessage[];
  searchFilters: SearchFilters;
  isLoading: boolean;
  isLoadingProjects: boolean;
  isLoadingSessions: boolean;
  isLoadingMessages: boolean;
  isLoadingTokenStats: boolean;
  error: AppError | null;
  sessionTokenStats: SessionTokenStats | null;
  projectTokenStats: SessionTokenStats[];
  messageOrder: MessageOrderType;
}

interface AppStore extends AppState {
  excludeSidechain: boolean;
  collapseNonUserMessages: boolean;
  analytics: AnalyticsState;

  // Actions
  initializeApp: () => Promise<void>;
  scanProjects: () => Promise<void>;
  selectProject: (project: ClaudeProject) => Promise<void>;
  selectSession: (session: ClaudeSession, pageSize?: number) => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  refreshCurrentSession: () => Promise<void>;
  searchMessages: (query: string, filters?: SearchFilters) => Promise<void>;
  setSearchFilters: (filters: SearchFilters) => void;
  setError: (error: AppError | null) => void;
  setClaudePath: (path: string) => void;
  loadSessionTokenStats: (sessionPath: string) => Promise<void>;
  loadProjectTokenStats: (projectPath: string) => Promise<void>;
  loadProjectStatsSummary: (projectPath: string) => Promise<ProjectStatsSummary>;
  loadSessionComparison: (sessionId: string, projectPath: string) => Promise<SessionComparison>;
  clearTokenStats: () => void;
  setExcludeSidechain: (exclude: boolean) => void;
  setMessageOrder: (order: MessageOrderType) => void;
  setCollapseNonUserMessages: (collapse: boolean) => void;

  // Analytics actions
  setAnalyticsCurrentView: (view: AnalyticsViewType) => void;
  setAnalyticsProjectSummary: (summary: ProjectStatsSummary | null) => void;
  setAnalyticsSessionComparison: (comparison: SessionComparison | null) => void;
  setAnalyticsLoadingProjectSummary: (loading: boolean) => void;
  setAnalyticsLoadingSessionComparison: (loading: boolean) => void;
  setAnalyticsProjectSummaryError: (error: string | null) => void;
  setAnalyticsSessionComparisonError: (error: string | null) => void;
  resetAnalytics: () => void;
  clearAnalyticsErrors: () => void;
}

const DEFAULT_PAGE_SIZE = 20; // Reduced for better performance

export const useAppStore = create<AppStore>((set, get) => ({
  // Initial state
  claudePath: "",
  projects: [],
  selectedProject: null,
  sessions: [],
  selectedSession: null,
  messages: [],
  pagination: {
    currentOffset: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    hasMore: false,
    isLoadingMore: false,
  },
  searchQuery: "",
  searchResults: [],
  searchFilters: {},
  isLoading: false,
  isLoadingProjects: false,
  isLoadingSessions: false,
  isLoadingMessages: false,
  isLoadingTokenStats: false,
  error: null,
  sessionTokenStats: null,
  projectTokenStats: [],
  excludeSidechain: true,
  messageOrder: "asc", // Default: oldest first (top to bottom)
  collapseNonUserMessages: (() => {
    // Load from localStorage on init
    try {
      const saved = localStorage.getItem("collapseNonUserMessages");
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  })(),
  analytics: initialAnalyticsState,

  // Actions
  initializeApp: async () => {
    set({ isLoading: true, error: null });
    try {
      // Health check
      const health = await api.healthCheck();

      if (!health.isValid) {
        throw new Error("Claude folder not found or invalid");
      }

      set({ claudePath: health.claudePath });
      await get().scanProjects();
    } catch (error) {
      console.error("Failed to initialize app:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);

      let errorType = AppErrorType.UNKNOWN;
      let message = errorMessage;

      if (errorMessage.includes("not found")) {
        errorType = AppErrorType.CLAUDE_FOLDER_NOT_FOUND;
      } else if (errorMessage.includes("network") || errorMessage.includes("fetch")) {
        errorType = AppErrorType.NETWORK_ERROR;
        message = "Cannot connect to server. Please ensure the server is running.";
      }

      set({ error: { type: errorType, message } });
    } finally {
      set({ isLoading: false });
    }
  },

  scanProjects: async () => {
    const { claudePath } = get();
    set({ isLoadingProjects: true, error: null });
    try {
      const projects = await api.scanProjects(claudePath);
      set({ projects });
    } catch (error) {
      console.error("Failed to scan projects:", error);
      set({ error: { type: AppErrorType.API_ERROR, message: String(error) } });
    } finally {
      set({ isLoadingProjects: false });
    }
  },

  selectProject: async (project: ClaudeProject) => {
    set({
      selectedProject: project,
      sessions: [],
      selectedSession: null,
      messages: [],
      isLoadingSessions: true,
    });
    try {
      const sessions = await api.loadProjectSessions(
        project.path,
        get().excludeSidechain
      );
      set({ sessions });
    } catch (error) {
      console.error("Failed to load project sessions:", error);
      set({ error: { type: AppErrorType.API_ERROR, message: String(error) } });
    } finally {
      set({ isLoadingSessions: false });
    }
  },

  selectSession: async (session: ClaudeSession, pageSize = DEFAULT_PAGE_SIZE) => {
    set({
      selectedSession: session,
      messages: [],
      pagination: {
        currentOffset: 0,
        pageSize,
        totalCount: 0,
        hasMore: false,
        isLoadingMore: false,
      },
      isLoadingMessages: true,
    });

    try {
      const order = get().messageOrder;
      const messagePage = await api.loadSessionMessagesPaginated(
        session.file_path,
        {
          offset: 0,
          limit: pageSize,
          excludeSidechain: get().excludeSidechain,
          order,
        }
      );

      set({
        messages: messagePage.messages,
        pagination: {
          currentOffset: messagePage.next_offset,
          pageSize,
          totalCount: messagePage.total_count,
          hasMore: messagePage.has_more,
          isLoadingMore: false,
        },
        isLoadingMessages: false,
      });
    } catch (error) {
      console.error("Failed to load session messages:", error);
      set({
        error: { type: AppErrorType.API_ERROR, message: String(error) },
        isLoadingMessages: false,
      });
    }
  },

  loadMoreMessages: async () => {
    const { selectedSession, pagination, messages } = get();

    if (!selectedSession || !pagination.hasMore || pagination.isLoadingMore) {
      return;
    }

    set({
      pagination: { ...pagination, isLoadingMore: true },
    });

    try {
      const order = get().messageOrder;
      const messagePage = await api.loadSessionMessagesPaginated(
        selectedSession.file_path,
        {
          offset: pagination.currentOffset,
          limit: pagination.pageSize,
          excludeSidechain: get().excludeSidechain,
          order,
        }
      );

      // Append messages based on order:
      // asc: new messages are newer, append to end
      // desc: new messages are older, prepend to beginning
      const newMessages = order === "asc"
        ? [...messages, ...messagePage.messages]
        : [...messagePage.messages, ...messages];

      set({
        messages: newMessages,
        pagination: {
          ...pagination,
          currentOffset: messagePage.next_offset,
          hasMore: messagePage.has_more,
          isLoadingMore: false,
        },
      });
    } catch (error) {
      console.error("Failed to load more messages:", error);
      set({
        error: { type: AppErrorType.API_ERROR, message: String(error) },
        pagination: { ...pagination, isLoadingMore: false },
      });
    }
  },

  searchMessages: async (query: string, filters: SearchFilters = {}) => {
    const { claudePath } = get();
    if (!claudePath || !query.trim()) {
      set({ searchResults: [], searchQuery: "" });
      return;
    }

    set({ isLoadingMessages: true, searchQuery: query });
    try {
      const results = await api.searchMessages(claudePath, query, filters as Record<string, unknown>);
      set({ searchResults: results });
    } catch (error) {
      console.error("Failed to search messages:", error);
      set({ error: { type: AppErrorType.API_ERROR, message: String(error) } });
    } finally {
      set({ isLoadingMessages: false });
    }
  },

  refreshCurrentSession: async () => {
    const { selectedProject, selectedSession, pagination } = get();

    if (!selectedSession) return;

    set({ error: null });

    try {
      if (selectedProject) {
        const sessions = await api.loadProjectSessions(
          selectedProject.path,
          get().excludeSidechain
        );
        set({ sessions });
      }

      await get().selectSession(selectedSession, pagination.pageSize);
    } catch (error) {
      console.error("Refresh failed:", error);
      set({ error: { type: AppErrorType.API_ERROR, message: String(error) } });
    }
  },

  setSearchFilters: (filters: SearchFilters) => {
    set({ searchFilters: filters });
  },

  setError: (error: AppError | null) => {
    set({ error });
  },

  setClaudePath: (path: string) => {
    set({ claudePath: path });
    // In web version, we don't persist settings locally
  },

  loadSessionTokenStats: async (sessionPath: string) => {
    try {
      set({ isLoadingTokenStats: true, error: null });
      const stats = await api.getSessionTokenStats(sessionPath);
      set({ sessionTokenStats: stats });
    } catch (error) {
      console.error("Failed to load session token stats:", error);
      set({
        error: { type: AppErrorType.API_ERROR, message: String(error) },
        sessionTokenStats: null,
      });
    } finally {
      set({ isLoadingTokenStats: false });
    }
  },

  loadProjectTokenStats: async (projectPath: string) => {
    try {
      set({ isLoadingTokenStats: true, error: null });
      // Extract project name from path
      const projectName = projectPath.split("/").pop() || projectPath;
      const stats = await api.getProjectTokenStats(projectName);
      set({ projectTokenStats: stats });
    } catch (error) {
      console.error("Failed to load project token stats:", error);
      set({
        error: { type: AppErrorType.API_ERROR, message: String(error) },
        projectTokenStats: [],
      });
    } finally {
      set({ isLoadingTokenStats: false });
    }
  },

  loadProjectStatsSummary: async (projectPath: string) => {
    const projectName = projectPath.split("/").pop() || projectPath;
    return api.getProjectStatsSummary(projectName);
  },

  loadSessionComparison: async (_sessionId: string, _projectPath: string) => {
    // This endpoint doesn't exist in web API yet, return mock data
    throw new Error("Session comparison not implemented in web version");
  },

  clearTokenStats: () => {
    set({ sessionTokenStats: null, projectTokenStats: [] });
  },

  setExcludeSidechain: (exclude: boolean) => {
    set({ excludeSidechain: exclude });
    const { selectedProject, selectedSession } = get();
    if (selectedProject) {
      get().selectProject(selectedProject);
    }
    if (selectedSession) {
      get().selectSession(selectedSession);
    }
  },

  setMessageOrder: (order: MessageOrderType) => {
    set({ messageOrder: order });
    // Reload current session with new order
    const { selectedSession, pagination } = get();
    if (selectedSession) {
      get().selectSession(selectedSession, pagination.pageSize);
    }
  },

  setCollapseNonUserMessages: (collapse: boolean) => {
    set({ collapseNonUserMessages: collapse });
    // Persist to localStorage
    try {
      localStorage.setItem("collapseNonUserMessages", JSON.stringify(collapse));
    } catch (error) {
      console.error("Failed to save collapseNonUserMessages setting:", error);
    }
  },

  // Analytics actions
  setAnalyticsCurrentView: (view: AnalyticsViewType) => {
    set((state) => ({
      analytics: { ...state.analytics, currentView: view },
    }));
  },

  setAnalyticsProjectSummary: (summary: ProjectStatsSummary | null) => {
    set((state) => ({
      analytics: { ...state.analytics, projectSummary: summary },
    }));
  },

  setAnalyticsSessionComparison: (comparison: SessionComparison | null) => {
    set((state) => ({
      analytics: { ...state.analytics, sessionComparison: comparison },
    }));
  },

  setAnalyticsLoadingProjectSummary: (loading: boolean) => {
    set((state) => ({
      analytics: { ...state.analytics, isLoadingProjectSummary: loading },
    }));
  },

  setAnalyticsLoadingSessionComparison: (loading: boolean) => {
    set((state) => ({
      analytics: { ...state.analytics, isLoadingSessionComparison: loading },
    }));
  },

  setAnalyticsProjectSummaryError: (error: string | null) => {
    set((state) => ({
      analytics: { ...state.analytics, projectSummaryError: error },
    }));
  },

  setAnalyticsSessionComparisonError: (error: string | null) => {
    set((state) => ({
      analytics: { ...state.analytics, sessionComparisonError: error },
    }));
  },

  resetAnalytics: () => {
    set({ analytics: initialAnalyticsState });
  },

  clearAnalyticsErrors: () => {
    set((state) => ({
      analytics: {
        ...state.analytics,
        projectSummaryError: null,
        sessionComparisonError: null,
      },
    }));
  },
}));
